
import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Modal, Row, Col } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { BsPencil } from 'react-icons/bs';
import { IconContext } from 'react-icons';
import CatEmpleados from '../../../Services/Catalogos/CatEmpleados';
import CatPermisos from '../../../Services/Catalogos/CatPermisos';
import { DTOPermiso } from '../CatPermisos/DTO/DTOPermiso';
import DTOEmpleado from '../CatEmpleados/DTO/DTOEmpleado';
import { MsgInformativo } from '../../../Utils/Toast/msgInformativo';
import { AxiosError } from 'axios';

const ControlPermisosRH = () => {
  const [empleados, setEmpleados] = useState<DTOEmpleado[]>([]);
  const [permisos, setPermisos] = useState<DTOPermiso[]>([]);
  const [selectedEmpleado, setSelectedEmpleado] = useState<string>('');
  const [filtroEmpleado, setFiltroEmpleado] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [permisoEditando, setPermisoEditando] = useState<DTOPermiso | null>(null);
  const [showModalNuevoPermiso, setShowModalNuevoPermiso] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [msg, setMsg] = useState('');
  const [header, setHeader] = useState('');
  const [showMsg, setShowMsg] = useState(false);
  const [procesandoPermiso, setProcesandoPermiso] = useState(false);
  const procesandoPermisoRef = useRef(false);
  const msgColor = header === 'Error' ? 'danger' : 'primary';

  // Estado para nuevo permiso - Estado por defecto: Aceptado
  const [nuevoPermiso, setNuevoPermiso] = useState<DTOPermiso>({
    id: 0,
    fechaPermiso: '',
    fechaSolicitud: new Date().toISOString().split('T')[0],
    nombreEmpleado: '',
    puestoEmpleado: '',
    motivo: '',
    horaInicio: '',
    horaFin: '',
    permisoConSueldo: false,
    autorizador: '',
    observaciones: '',
    estado: 'Aceptado', // Cambiado de 'Pendiente' a 'Aceptado'
    fechaAceptacionPermiso: null,
    correo: '',
    numEmpleado: '',
    esDiaCompleto: false,
  });

  // Cargar todos los empleados al montar el componente
  useEffect(() => {
    cargarEmpleados();
  }, []);

  // Cargar permisos cuando se selecciona un empleado
  useEffect(() => {
    if (!selectedEmpleado) {
      setPermisos([]);
      return;
    }
    cargarPermisos(selectedEmpleado);
    
    // Actualizar datos del empleado seleccionado para el nuevo permiso
    const empleadoSeleccionado = empleados.find(e => e.numEmpleado === selectedEmpleado);
    if (empleadoSeleccionado) {
      setNuevoPermiso(prev => ({
        ...prev,
        numEmpleado: empleadoSeleccionado.numEmpleado,
        nombreEmpleado: empleadoSeleccionado.name,
        // puestoEmpleado: empleadoSeleccionado.puesto || '',
        // correo: empleadoSeleccionado.correo || ''
      }));
    }
  }, [selectedEmpleado, empleados]);

  // Aplicar estilos dinámicos
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerText = styles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const cargarEmpleados = async () => {
    setCargando(true);
    try {
      const empleadosData = await CatEmpleados.GetAllEmpleado();
      setEmpleados(empleadosData.data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
      setHeader('Error');
      setMsg( 'No se pudieron cargar los empleados.');
      setShowMsg(true);
    } finally {
      setCargando(false);
    }
  };

  const cargarPermisos = async (numEmpleado: string) => {
    try {
      const res = await CatPermisos.GetPermisosPorEmpleado(numEmpleado);
      setPermisos(res.data || []);
    } catch (error) {
      console.error('Error al cargar permisos:', error);
      setHeader('Error');
      setMsg('No se pudieron cargar los permisos.');
      setShowMsg(true);
    }
  };

  const handleEditPermiso = (permiso: DTOPermiso) => {
    setPermisoEditando(permiso);
    setShowModal(true);
  };

  const guardarEstadoPermiso = async () => {
    if (procesandoPermisoRef.current) return;
    if (!permisoEditando) return;

    procesandoPermisoRef.current = true;
    setProcesandoPermiso(true);

    try {
      // Validar fecha del permiso si es "Aceptado"
    //   if (permisoEditando.estado === 'Aceptado') {
    //     const today = new Date().toISOString().split('T')[0];
    //     if (permisoEditando.fechaPermiso < today) {
    //       setHeader('Error');
    //       setMsg('La fecha del permiso no puede ser anterior a la fecha actual.');
    //       setShowMsg(true);
    //       return;
    //     }
    //   }

      if (permisoEditando.estado === 'Rechazado') {
        await CatPermisos.DeletePermiso(permisoEditando.id);
        setPermisos((prev) => prev.filter((p) => p.id !== permisoEditando.id));

        if (permisoEditando.correo) {
          await CatPermisos.EnviarCorreoPermiso({
            id: permisoEditando.id,
            numEmpleado: permisoEditando.numEmpleado,
            nombreEmpleado: permisoEditando.nombreEmpleado || '',
            correo: permisoEditando.correo,
            motivo: permisoEditando.motivo || '',
            fechaPermiso: permisoEditando.fechaPermiso || '',
            estado: 'Rechazado',
            observaciones: permisoEditando.observaciones || '',
          });
        }

        setHeader('Permiso Rechazado');
        setMsg('Permiso eliminado correctamente');
        setShowMsg(true);
      } else {
        const respuestaEstado = await CatPermisos.PutEstadoPermiso(
          permisoEditando.id,
          permisoEditando.estado,
          permisoEditando.permisoConSueldo ?? false,
          permisoEditando.observaciones || ''
        );

        const estadoModificado = respuestaEstado.data?.[0]?.estadoModificado ?? false;

        if (estadoModificado && permisoEditando.estado === 'Aceptado' && permisoEditando.correo) {
          await CatPermisos.EnviarCorreoPermiso({
            id: permisoEditando.id,
            numEmpleado: permisoEditando.numEmpleado,
            nombreEmpleado: permisoEditando.nombreEmpleado || '',
            correo: permisoEditando.correo,
            motivo: permisoEditando.motivo || '',
            fechaPermiso: permisoEditando.fechaPermiso || '',
            estado: 'Aceptado',
            observaciones: permisoEditando.observaciones || '',
          });
          await CatPermisos.EnviarCorreoRH(permisoEditando);
        }

        await cargarPermisos(permisoEditando.numEmpleado);  
        setHeader('Estado actualizado');
        setMsg('El estado del permiso fue actualizado correctamente.');
        setShowMsg(true);
      }

      setShowModal(false);
      setPermisoEditando(null);
    } catch (error) {
      console.error('Error al actualizar permiso:', error);
      setHeader('Error');
      setMsg('Ocurrió un error al guardar los cambios.');
      setShowMsg(true);
    } finally {
      procesandoPermisoRef.current = false;
      setProcesandoPermiso(false);
    }
  };



// Función para insertar un nuevo permiso
const insertarPermiso = async () => {
  if (procesandoPermisoRef.current) return;
  if (!selectedEmpleado) {
    setHeader('Error');
    setMsg('Debe seleccionar un empleado primero.');
    setShowMsg(true);
    return;
  }

  if (!nuevoPermiso.fechaPermiso || !nuevoPermiso.motivo) {
    setHeader('Atención');
    setMsg('Faltan campos obligatorios como Fecha y Motivo.');
    setShowMsg(true);
    return;
  }

//   Validación de fecha: No permitir fechas mayores a 1 semana en el pasado
//   const fechaPermiso = new Date(nuevoPermiso.fechaPermiso);
//   const fechaActual = new Date();
//   const unaSemanaAtras = new Date();
//   unaSemanaAtras.setDate(fechaActual.getDate() - 7);
  
//   // Normalizar fechas (solo día, sin hora)
//   fechaPermiso.setHours(0, 0, 0, 0);
//   unaSemanaAtras.setHours(0, 0, 0, 0);
//   fechaActual.setHours(0, 0, 0, 0);

//   if (fechaPermiso < unaSemanaAtras) {
//     setHeader('Error de fecha');
//     setMsg('Solo se pueden registrar permisos con fecha hasta una semana antes de la fecha actual.');
//     setShowMsg(true);
//     return;
//   }

  // Validaciones de horario si no es día completo
  if (!nuevoPermiso.esDiaCompleto) {
    const horaInicio = nuevoPermiso.horaInicio;
    const horaFin = nuevoPermiso.horaFin;

    if (!horaInicio || !horaFin) {
      setHeader('Horario requerido');
      setMsg('Debes ingresar hora de salida y regreso.');
      setShowMsg(true);
      return;
    }

    const horaMinima = '08:00';
    const horaMaxima = '20:00';

    if (horaInicio < horaMinima || horaInicio > horaMaxima) {
      setHeader('Hora de salida inválida');
      setMsg('La hora de salida debe estar entre 08:00.');
      setShowMsg(true);
      return;
    }
    
    if (horaFin < horaMinima || horaFin > horaMaxima) {
      setHeader('Hora de regreso inválida');
      setMsg('La hora de regreso solo es aplicable antes de 18:00.');
      setShowMsg(true);
      return;
    }

    if (horaFin <= horaInicio) {
      setHeader('Orden de horas inválido');
      setMsg('La hora de regreso debe ser posterior a la hora de salida.');
      setShowMsg(true);
      return;
    }
  }

  procesandoPermisoRef.current = true;
  setProcesandoPermiso(true);

  try {
    const permisoAInsertar: DTOPermiso = {
      ...nuevoPermiso,
      numEmpleado: selectedEmpleado
    };

    // Insertar el permiso
    const response = await CatPermisos.PosInsertarPermiso(permisoAInsertar);
    const permisoInsertadoBase = response.data?.[0];
    const permisoInsertado: DTOPermiso = {
      ...permisoAInsertar,
      ...permisoInsertadoBase,
      correo: permisoInsertadoBase?.correo || permisoAInsertar.correo,
    };
    
    // Enviar correo automáticamente ya que el permiso es "Aceptado" por defecto
    
      try {
        await CatPermisos.EnviarCorreoPermiso({
          id: permisoInsertado.id,
          numEmpleado: permisoInsertado.numEmpleado,
          nombreEmpleado: permisoInsertado.nombreEmpleado || '',
          correo: permisoInsertado.correo,
          motivo: permisoInsertado.motivo || '',
          fechaPermiso: permisoInsertado.fechaPermiso || '',
          estado: 'Aceptado',
          observaciones: permisoInsertado.observaciones || '',
        });
        
        // También enviar correo a RH
        await CatPermisos.EnviarCorreoRH(permisoInsertado);
      } catch (emailError) {
        console.error('Error al enviar correo:', emailError);
        // No detenemos el proceso si falla el envío de correo
      }


    


    

    
    
    setShowModalNuevoPermiso(false);
    setHeader('Éxito');
    setMsg('Permiso registrado y aceptado correctamente.');
    setShowMsg(true);
    
    // Recargar los permisos del empleado
    await cargarPermisos(selectedEmpleado);
    
    // Limpiar formulario (manteniendo el estado como Aceptado)
    setNuevoPermiso({
      id: 0,
      fechaPermiso: '',
      fechaSolicitud: new Date().toISOString().split('T')[0],
      nombreEmpleado: nuevoPermiso.nombreEmpleado,
      puestoEmpleado: nuevoPermiso.puestoEmpleado,
      motivo: '',
      horaInicio: '',
      horaFin: '',
      permisoConSueldo: false,
      autorizador: '',
      observaciones: '',
      estado: 'Aceptado', // Mantener como Aceptado
      fechaAceptacionPermiso: null,
      correo: nuevoPermiso.correo,
      numEmpleado: selectedEmpleado,
      esDiaCompleto: false,
    });
  } catch (error: any) {
    console.error('Error al insertar permiso:', error);
    setHeader('Error');
    setMsg(error.response?.data?.message || 'Error al insertar permiso.');
    setShowMsg(true);
  } finally {
    procesandoPermisoRef.current = false;
    setProcesandoPermiso(false);
  }
};


  const formatearFecha = (fecha: string) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    const dia = date.getDate().toString().padStart(2, '0');
    const mes = (date.getMonth() + 1).toString().padStart(2, '0');
    const anio = date.getFullYear();
    return `${dia}/${mes}/${anio}`;
  };

  const columns = [
    {
      name: 'Fecha Permiso',
      selector: (row: DTOPermiso) => formatearFecha(row.fechaPermiso),
      sortable: true,
      width: '120px',
    },
    {
      name: 'Motivo',
      selector: (row: DTOPermiso) => row.motivo,
      width: '150px',
    },
    {
      name: 'Horario',
      selector: (row: DTOPermiso) => (row.esDiaCompleto ? 'Día completo' : `${row.horaInicio} - ${row.horaFin}`),
      width: '120px',
    },
    {
      name: 'Estado',
      cell: (row: DTOPermiso) => (
        <span
          className={`estado-${row.estado.toLowerCase()}`}
        >
          {row.estado}
        </span>
      ),
      sortable: true,
      width: '120px',
    },
    {
      name: 'Observaciones',
      selector: (row: DTOPermiso) => row.observaciones,
      cell: (row: DTOPermiso) => (
        <div
          title={row.observaciones}
          style={{ cursor: 'pointer' }}
          onDoubleClick={() => alert(`Observaciones:\n${row.observaciones}`)}
        >
          {row.observaciones && row.observaciones.length > 30
            ? row.observaciones.slice(0, 30) + '...'
            : row.observaciones}
        </div>
      ),
      width: '150px',
    },
    {
      name: 'Con Sueldo',
      selector: (row: DTOPermiso) => (row.permisoConSueldo ? 'Sí' : 'No'),
      width: '100px',
    },
    {
      name: 'Editar',
      cell: (row: DTOPermiso) => (
        <IconContext.Provider value={{ color: 'blue', size: '16px' }}>
          <BsPencil
            style={{ cursor: 'pointer' }}
            onClick={() => handleEditPermiso(row)}
          />
        </IconContext.Provider>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '80px',
    },
  ];

  const styles = `
    .estado-aceptado {
      padding: 4px 8px;
      border-radius: 12px;
      font-weight: bold;
      color: white;
      background-color: green;
    }
    .estado-pendiente {
      padding: 4px 8px;
      border-radius: 12px;
      font-weight: bold;
      color: white;
      background-color: orange;
    }
    .estado-rechazado {
      padding: 4px 8px;
      border-radius: 12px;
      font-weight: bold;
      color: white;
      background-color: red;
    }
  `;

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Row>
          {/* <Col>Control de Permisos</Col> */}
          <Col className="text-end">
            <Button 
              variant="success" 
              size="sm" 
              onClick={() => setShowModalNuevoPermiso(true)}
              disabled={!selectedEmpleado}
              title={!selectedEmpleado ? "Seleccione un empleado primero" : "Agregar nuevo permiso"}
            >
              Nuevo Permiso
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {cargando ? (
          <p>Cargando empleados...</p>
        ) : (
          <>
            <Row className="align-items-end mb-4">
              <Col md={6}>
                <Form.Control
                  type="text"
                  placeholder="Buscar por nombre o número"
                  value={filtroEmpleado}
                  onChange={(e) => setFiltroEmpleado(e.target.value)}
                  className="mb-2"
                />
                <Form.Control
                  as="select"
                  value={selectedEmpleado}
                  onChange={(e) => setSelectedEmpleado(e.target.value)}
                  style={{ height: '38px' }}
                >
                  <option value="">-- Seleccionar --</option>
                  {empleados
                    .filter((e) =>
                      `${e.numEmpleado} ${e.name}`.toLowerCase().includes(filtroEmpleado.toLowerCase())
                    )
                    .map((e) => (
                      <option key={e.numEmpleado} value={e.numEmpleado}>
                        {e.numEmpleado} — {e.name}
                      </option>
                    ))}
                </Form.Control>
              </Col>
            </Row>

            {selectedEmpleado && (
              <DataTable
                columns={columns}
                data={permisos}
                pagination
                highlightOnHover
                noDataComponent="No hay permisos para este empleado."
              />
            )}
          </>
        )}
      </Card.Body>

      {/* Modal para editar permiso existente */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Permiso</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={permisoEditando?.estado || ''}
                onChange={(e) =>
                  setPermisoEditando((prev) =>
                    prev ? { ...prev, estado: e.target.value } : null
                  )
                }
              >
                <option value="Pendiente">Pendiente</option>
                <option value="Aceptado">Aceptado</option>
                <option value="Rechazado">Rechazado</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Goce de Sueldo</Form.Label>
              <Form.Check
                type="checkbox"
                checked={permisoEditando?.permisoConSueldo || false}
                onChange={(e) =>
                  setPermisoEditando((prev) =>
                    prev ? { ...prev, permisoConSueldo: e.target.checked } : null
                  )
                }
                label="¿Con goce de sueldo?"
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={permisoEditando?.observaciones || ''}
                onChange={(e) =>
                  setPermisoEditando((prev) =>
                    prev ? { ...prev, observaciones: e.target.value.slice(0, 500) } : null
                  )
                }
                maxLength={500}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={procesandoPermiso}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={guardarEstadoPermiso}
            disabled={!permisoEditando || !permisoEditando.estado || procesandoPermiso}
          >
            {procesandoPermiso ? 'Guardando...' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para nuevo permiso */}
      <Modal show={showModalNuevoPermiso} onHide={() => setShowModalNuevoPermiso(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nuevo Permiso para {nuevoPermiso.nombreEmpleado}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha del Permiso *</Form.Label>
                  <Form.Control
                    type="date"
                    value={nuevoPermiso.fechaPermiso}
                    onChange={(e) => setNuevoPermiso({ ...nuevoPermiso, fechaPermiso: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Empleado</Form.Label>
                  <Form.Control type="text" value={nuevoPermiso.numEmpleado} readOnly />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control type="text" value={nuevoPermiso.nombreEmpleado} readOnly />
                </Form.Group>
              </Col>
              {/* <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Puesto</Form.Label>
                  <Form.Control type="text" value={nuevoPermiso.puestoEmpleado} readOnly />
                </Form.Group>
              </Col> */}
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Motivo *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={nuevoPermiso.motivo}
                onChange={(e) => setNuevoPermiso({ ...nuevoPermiso, motivo: e.target.value })}
                placeholder="Describa el motivo del permiso"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="¿Es día completo?"
                checked={nuevoPermiso.esDiaCompleto}
                onChange={(e) => setNuevoPermiso({ ...nuevoPermiso, esDiaCompleto: e.target.checked })}
              />
            </Form.Group>

            {!nuevoPermiso.esDiaCompleto && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hora de salida *</Form.Label>
                    <Form.Control
                      type="time"
                      value={nuevoPermiso.horaInicio}
                      onChange={(e) => setNuevoPermiso({ ...nuevoPermiso, horaInicio: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hora de regreso *</Form.Label>
                    <Form.Control
                      type="time"
                      value={nuevoPermiso.horaFin}
                      onChange={(e) => setNuevoPermiso({ ...nuevoPermiso, horaFin: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="¿Con goce de sueldo?"  
                checked={nuevoPermiso.permisoConSueldo}
                onChange={(e) => setNuevoPermiso({ ...nuevoPermiso, permisoConSueldo: e.target.checked })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={nuevoPermiso.observaciones}
                onChange={(e) => setNuevoPermiso({ ...nuevoPermiso, observaciones: e.target.value })}
                placeholder="Observaciones adicionales (opcional)"
              />
            </Form.Group>

            {/* <Form.Group className="mb-3">
              <Form.Label>Estado del Permiso</Form.Label>
              <Form.Control 
                type="text" 
                value="Aceptado" 
                readOnly 
                className="fw-bold text-success"
              />
              <Form.Text className="text-muted">
                Los permisos creados desde RH se registran automáticamente como Aceptados
              </Form.Text>
            </Form.Group> */}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalNuevoPermiso(false)} disabled={procesandoPermiso}>Cancelar</Button>
          <Button variant="primary" onClick={insertarPermiso} disabled={procesandoPermiso}>
            {procesandoPermiso ? 'Guardando...' : 'Guardar Permiso'}
          </Button>
        </Modal.Footer>
      </Modal>

      <MsgInformativo
        show={showMsg}
        msg={msg}
        header={header}
        msgColor={msgColor}
        closeToast={() => setShowMsg(false)}
      />
    </Card>
  );
};

export default ControlPermisosRH;
